import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Save, Edit, Lock, User, Mail, Phone, Calendar, Shield,
  Bell, Download, Trash2, Settings, Eye, EyeOff, Smartphone,
  Globe, FileText, AlertTriangle, CheckCircle, Clock,
  CreditCard, BarChart3, Activity, Key, Database,
  Upload, Camera, MapPin, Languages, Palette,
  HelpCircle, RefreshCw, X
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/store/auth';
import { customerApi, CustomerProfile, CustomerProfileUpdate } from '@/api/customers';
import { apiClient } from '@/api/client';
import toast from 'react-hot-toast';

const profileSchema = z.object({
  first_name: z.string().min(2, 'Der Vorname muss mindestens 2 Zeichen lang sein'),
  last_name: z.string().min(2, 'Der Nachname muss mindestens 2 Zeichen lang sein'),
  phone: z.string().min(10, 'Die Telefonnummer muss mindestens 10 Ziffern enthalten'),
  date_of_birth: z.string().optional(),
  national_id: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  postal_code: z.string().optional(),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Aktuelles Passwort ist erforderlich'),
  newPassword: z.string()
    .min(8, 'Neues Passwort muss mindestens 8 Zeichen lang sein')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Passwort muss mindestens einen Großbuchstaben, Kleinbuchstaben und eine Zahl enthalten'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwörter stimmen nicht überein',
  path: ['confirmPassword'],
});

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

interface NotificationSettings {
  email_notifications: boolean;
  sms_notifications: boolean;
  investment_alerts: boolean;
  marketing_emails: boolean;
  security_alerts: boolean;
}

interface PrivacySettings {
  profile_visibility: 'public' | 'private';
  data_sharing: boolean;
  analytics_tracking: boolean;
  personalized_ads: boolean;
}

interface SecurityEvent {
  id: number;
  type: 'login' | 'password_change' | 'profile_update' | 'logout';
  description: string;
  ip_address: string;
  device: string;
  location: string;
  timestamp: string;
}

const fieldLabelCls = "block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5";
const disabledInputCls = "w-full px-3 py-2 border border-slate-300 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none disabled:bg-slate-50 disabled:text-slate-500";

const ProfilePage = () => {
  const { user, setUser } = useAuthStore();
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Tab states
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Notification and privacy settings
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    email_notifications: true,
    sms_notifications: false,
    investment_alerts: true,
    marketing_emails: false,
    security_alerts: true
  });

  const [privacySettings, setPrivacySettings] = useState<PrivacySettings>({
    profile_visibility: 'private',
    data_sharing: false,
    analytics_tracking: true,
    personalized_ads: false
  });

  // Forms
  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      phone: '',
      date_of_birth: '',
      national_id: '',
      address: '',
      city: '',
      country: '',
      postal_code: ''
    }
  });

  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
  });

  // Fetch profile data from API
  const fetchProfile = async () => {
    try {
      setError(null);
      const isCustomer = user?.role === 'customer';
      if (isCustomer) {
        const profileData = await customerApi.profile.get();
        setProfile(profileData);
        profileForm.reset({
          first_name: profileData.first_name || '',
          last_name: profileData.last_name || '',
          phone: profileData.phone || '',
          date_of_birth: profileData.date_of_birth || '',
          national_id: profileData.national_id || '',
          address: profileData.address || '',
          city: profileData.city || '',
          country: profileData.country || '',
          postal_code: profileData.postal_code || ''
        });
        setNotificationSettings(prev => ({
          ...prev,
          email_notifications: profileData.email_notifications,
          sms_notifications: profileData.sms_notifications
        }));
      } else {
        // Non-customer roles: use users profile endpoints
        const response = await apiClient.get('/api/v1/users/profile/me');
        const u = response.data || {};
        const mapped: CustomerProfile = {
          id: u.id,
          email: u.email,
          first_name: u.first_name || '',
          last_name: u.last_name || '',
          phone: u.phone || '',
          date_of_birth: '',
          national_id: '',
          address: '',
          city: '',
          country: '',
          postal_code: '',
          email_notifications: false,
          sms_notifications: false,
          two_factor_enabled: false,
          created_at: u.created_at || new Date().toISOString(),
          updated_at: u.updated_at || undefined,
        } as CustomerProfile;
        setProfile(mapped);
        profileForm.reset({
          first_name: mapped.first_name,
          last_name: mapped.last_name,
          phone: mapped.phone,
          date_of_birth: '',
          national_id: '',
          address: '',
          city: '',
          country: '',
          postal_code: ''
        });
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Fehler beim Laden der Profildaten';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error fetching profile:', err);
    }
  };

  // Initial load
  useEffect(() => {
    const loadProfile = async () => {
      setIsLoading(true);
      try {
        await fetchProfile();
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  // Refresh data
  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      await fetchProfile();
      toast.success('Profildaten wurden aktualisiert');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Update profile
  const onSubmitProfile = async (data: ProfileFormData) => {
    setSaving(true);
    try {
      const isCustomer = user?.role === 'customer';
      let updatedProfile: any;
      if (isCustomer) {
        updatedProfile = await customerApi.profile.update(data);
      } else {
        // Map to users profile update
        const payload: any = {
          first_name: data.first_name,
          last_name: data.last_name,
          phone: data.phone,
        };
        const response = await apiClient.put('/api/v1/users/profile/me', payload);
        const u = response.data || {};
        updatedProfile = {
          ...profile,
          first_name: u.first_name || data.first_name,
          last_name: u.last_name || data.last_name,
          phone: u.phone || data.phone,
          email: u.email || profile?.email,
        };
      }
      setProfile(updatedProfile);
      setIsEditing(false);
      if (setUser && user) {
        setUser({
          ...user,
          name: `${updatedProfile.first_name} ${updatedProfile.last_name}`,
          email: updatedProfile.email
        });
      }
      toast.success('Profil wurde erfolgreich aktualisiert');
    } catch (err: any) {
      const errorMessage = err.message || 'Fehler beim Aktualisieren des Profils';
      toast.error(errorMessage);
      console.error('Error updating profile:', err);
    } finally {
      setSaving(false);
    }
  };

  // Change password
  const onSubmitPassword = async (data: PasswordFormData) => {
    setIsChangingPassword(true);
    try {
      // Tüm roller için çalışan endpoint (get_current_active_user)
      await apiClient.post('/api/v1/users/change-password', {
        current_password: data.currentPassword,
        new_password: data.newPassword,
      });
      toast.success('Passwort wurde erfolgreich geändert');
      passwordForm.reset();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.detail || err.message || 'Fehler beim Ändern des Passworts';
      toast.error(errorMessage);
      console.error('Error changing password:', err);
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Update notification settings
  const updateNotificationSettings = async (newSettings: Partial<NotificationSettings>) => {
    try {
      const updatedSettings = { ...notificationSettings, ...newSettings };
      setNotificationSettings(updatedSettings);

      // Update profile with notification preferences
      await customerApi.profile.update({
        email_notifications: updatedSettings.email_notifications,
        sms_notifications: updatedSettings.sms_notifications
      });

      toast.success('Bildirim ayarları güncellendi');
    } catch (err: any) {
      toast.error('Bildirim ayarları güncellenirken hata oluştu');
      console.error('Error updating notification settings:', err);
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setIsEditing(false);
    if (profile) {
      profileForm.reset({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        date_of_birth: profile.date_of_birth || '',
        national_id: profile.national_id || '',
        address: profile.address || '',
        city: profile.city || '',
        country: profile.country || '',
        postal_code: profile.postal_code || ''
      });
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-2 border-primary-100 border-t-primary-600 mx-auto mb-4"></div>
          <p className="text-slate-500 text-sm">Profildaten werden geladen...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-14 h-14 bg-danger-50 border border-danger-200 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-7 h-7 text-danger-600" />
          </div>
          <h3 className="text-lg font-serif font-medium text-slate-900 mb-1">Profil konnte nicht geladen werden</h3>
          <p className="text-sm text-slate-500 mb-4">Beim Laden der Profildaten ist ein Fehler aufgetreten.</p>
          <Button onClick={refreshData}>Erneut versuchen</Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'profile', name: 'Profilinformationen', icon: User },
    { id: 'security', name: 'Sicherheit', icon: Shield },
  ];

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {error && (
        <div className="bg-white border border-slate-200 border-l-4 border-l-danger-600 p-4 flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-danger-600 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-danger-800">Fehler</h3>
              <p className="text-sm text-danger-700">{error}</p>
            </div>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-danger-600 hover:text-danger-800 w-7 h-7 flex items-center justify-center flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-5 border-b border-slate-200">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-600 mb-1.5 block">Konto</span>
          <h1 className="text-2xl font-serif font-medium text-slate-900">Profileinstellungen</h1>
          <p className="text-sm text-slate-500 mt-1">Verwalten Sie Ihre Kontoinformationen und Präferenzen</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={refreshData} disabled={isRefreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Profile Summary Card */}
      <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
        <div className="flex items-center space-x-6">
          <div className="w-20 h-20 bg-primary-50 border border-primary-100 flex items-center justify-center flex-shrink-0">
            <User className="w-10 h-10 text-primary-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-serif font-medium text-slate-900">
              {profile.first_name} {profile.last_name}
            </h2>
            <p className="text-slate-500 text-sm mt-0.5">{profile.email}</p>
            <div className="mt-3">
              <span className="text-xs text-slate-400">Beitritt: {formatDate(profile.created_at)}</span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {profile.two_factor_enabled && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 bg-success-50 text-success-700 border border-success-200 text-xs font-medium">
                <Shield className="w-3.5 h-3.5" />
                2FA Aktiv
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200">
        {/* Tab Navigation */}
        <div className="border-b border-slate-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-primary-600 text-primary-700'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Profile Information Tab (read-only) */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Persönliche Informationen</h3>
              </div>

              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={fieldLabelCls}>Vorname</label>
                    <input
                      {...profileForm.register('first_name')}
                      disabled
                      className={disabledInputCls}
                    />
                    {profileForm.formState.errors.first_name && (
                      <p className="text-danger-600 text-sm mt-1">{profileForm.formState.errors.first_name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className={fieldLabelCls}>Nachname</label>
                    <input
                      {...profileForm.register('last_name')}
                      disabled
                      className={disabledInputCls}
                    />
                    {profileForm.formState.errors.last_name && (
                      <p className="text-danger-600 text-sm mt-1">{profileForm.formState.errors.last_name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className={fieldLabelCls}>E-Mail</label>
                    <input
                      type="email"
                      value={profile.email}
                      disabled
                      className={disabledInputCls}
                    />
                  </div>

                  <div>
                    <label className={fieldLabelCls}>Telefon</label>
                    <input
                      {...profileForm.register('phone')}
                      disabled
                      className={disabledInputCls}
                    />
                    {profileForm.formState.errors.phone && (
                      <p className="text-danger-600 text-sm mt-1">{profileForm.formState.errors.phone.message}</p>
                    )}
                  </div>

                  <div>
                    <label className={fieldLabelCls}>Geburtsdatum</label>
                    <input
                      type="text"
                      value={(() => { const v = profileForm.watch('date_of_birth'); if (!v) return ''; const d = new Date(v); return isNaN(d.getTime()) ? v : d.toLocaleDateString('de-DE', {day:'2-digit',month:'2-digit',year:'numeric'}); })()}
                      disabled
                      readOnly
                      className={disabledInputCls}
                    />
                  </div>

                  <div>
                    <label className={fieldLabelCls}>Ausweisnummer</label>
                    <input
                      {...profileForm.register('national_id')}
                      disabled
                      className={disabledInputCls}
                    />
                  </div>
                </div>

                <div>
                  <label className={fieldLabelCls}>Adresse</label>
                  <textarea
                    {...profileForm.register('address')}
                    disabled
                    rows={3}
                    className={disabledInputCls}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className={fieldLabelCls}>Stadt</label>
                    <input
                      {...profileForm.register('city')}
                      disabled
                      className={disabledInputCls}
                    />
                  </div>

                  <div>
                    <label className={fieldLabelCls}>Land</label>
                    <input
                      {...profileForm.register('country')}
                      disabled
                      className={disabledInputCls}
                    />
                  </div>

                  <div>
                    <label className={fieldLabelCls}>Postleitzahl</label>
                    <input
                      {...profileForm.register('postal_code')}
                      disabled
                      className={disabledInputCls}
                    />
                  </div>
                </div>

                {/* Referenzkonto (Reference Account) */}
                {((profile as any)?.referenzkonto_iban || (profile as any)?.referenzkonto_bank) && (
                  <div className="space-y-3 pt-6 border-t border-slate-200">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <CreditCard className="w-3.5 h-3.5" />
                      Referenzkonto (Bankverbindung)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className={fieldLabelCls}>IBAN</label>
                        <input
                          disabled
                          value={(profile as any).referenzkonto_iban || '—'}
                          className={disabledInputCls + " font-mono"}
                        />
                      </div>
                      <div>
                        <label className={fieldLabelCls}>Bank</label>
                        <input
                          disabled
                          value={(profile as any).referenzkonto_bank || '—'}
                          className={disabledInputCls}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Gemeinschaftskonto — Partner Info */}
                {(profile as any)?.is_joint_account && (
                  <div className="pt-6 border-t border-slate-200">
                    <h3 className="text-base font-serif font-medium text-slate-900 mb-4 flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 bg-primary-50 text-primary-700 border border-primary-200 text-xs font-semibold">Gemeinschaftskonto</span>
                      Mitinhaber — Persönliche Daten
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className={fieldLabelCls}>Vorname</label>
                        <input disabled value={(profile as any).partner_first_name || '—'} className={disabledInputCls}/>
                      </div>
                      <div>
                        <label className={fieldLabelCls}>Nachname</label>
                        <input disabled value={(profile as any).partner_last_name || '—'} className={disabledInputCls}/>
                      </div>
                      <div>
                        <label className={fieldLabelCls}>Geburtsdatum</label>
                        <input disabled value={(profile as any).partner_birth_date ? new Date((profile as any).partner_birth_date).toLocaleDateString('de-DE', {day:'2-digit',month:'2-digit',year:'numeric'}) : '—'} className={disabledInputCls}/>
                      </div>
                      <div>
                        <label className={fieldLabelCls}>Ausweisnummer</label>
                        <input disabled value={(profile as any).partner_national_id || '—'} className={disabledInputCls}/>
                      </div>
                      <div className="md:col-span-2">
                        <label className={fieldLabelCls}>Adresse</label>
                        <input disabled value={[(profile as any).partner_address, (profile as any).partner_postal_code, (profile as any).partner_city, (profile as any).partner_country].filter(Boolean).join(', ') || '—'} className={disabledInputCls}/>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 border-b border-slate-200">Sicherheitseinstellungen</h3>

              {/* Change Password Section */}
              <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
                <h4 className="text-sm font-serif font-medium text-slate-900 mb-4">Passwort ändern</h4>
                <form onSubmit={passwordForm.handleSubmit(onSubmitPassword)} className="space-y-4">
                  <div>
                    <label className={fieldLabelCls}>Aktuelles Passwort</label>
                    <div className="relative">
                      <input
                        type={showPassword.current ? 'text' : 'password'}
                        {...passwordForm.register('currentPassword')}
                        className="w-full px-3 py-2 pr-10 border border-slate-300 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(prev => ({ ...prev, current: !prev.current }))}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPassword.current ? (
                          <EyeOff className="h-4 w-4 text-slate-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-slate-400" />
                        )}
                      </button>
                    </div>
                    {passwordForm.formState.errors.currentPassword && (
                      <p className="text-danger-600 text-sm mt-1">{passwordForm.formState.errors.currentPassword.message}</p>
                    )}
                  </div>

                  <div>
                    <label className={fieldLabelCls}>Neues Passwort</label>
                    <div className="relative">
                      <input
                        type={showPassword.new ? 'text' : 'password'}
                        {...passwordForm.register('newPassword')}
                        className="w-full px-3 py-2 pr-10 border border-slate-300 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(prev => ({ ...prev, new: !prev.new }))}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPassword.new ? (
                          <EyeOff className="h-4 w-4 text-slate-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-slate-400" />
                        )}
                      </button>
                    </div>
                    {passwordForm.formState.errors.newPassword && (
                      <p className="text-danger-600 text-sm mt-1">{passwordForm.formState.errors.newPassword.message}</p>
                    )}
                  </div>

                  <div>
                    <label className={fieldLabelCls}>Neues Passwort (Wiederholung)</label>
                    <div className="relative">
                      <input
                        type={showPassword.confirm ? 'text' : 'password'}
                        {...passwordForm.register('confirmPassword')}
                        className="w-full px-3 py-2 pr-10 border border-slate-300 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(prev => ({ ...prev, confirm: !prev.confirm }))}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPassword.confirm ? (
                          <EyeOff className="h-4 w-4 text-slate-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-slate-400" />
                        )}
                      </button>
                    </div>
                    {passwordForm.formState.errors.confirmPassword && (
                      <p className="text-danger-600 text-sm mt-1">{passwordForm.formState.errors.confirmPassword.message}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    variant="default"
                    disabled={isChangingPassword}
                  >
                    {isChangingPassword ? (
                      <div className="flex items-center">
                        <div className="animate-spin h-4 w-4 border-2 border-white/40 border-t-white mr-2"></div>
                        Wird geändert...
                      </div>
                    ) : (
                      <>
                        <Lock className="w-4 h-4 mr-2" />
                        Passwort ändern
                      </>
                    )}
                  </Button>
                </form>
              </div>

              {/* Two Factor Authentication (hidden for customers) */}
              {user?.role !== 'customer' && (
                <div className="bg-white border border-slate-200 border-t-2 border-t-gold-500 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-serif font-medium text-slate-900">İki Faktörlü Kimlik Doğrulama</h4>
                      <p className="text-sm text-slate-500 mt-1">
                        Hesabınızın güvenliğini artırmak için ek doğrulama katmanı ekleyin
                      </p>
                    </div>
                    <div className="flex items-center">
                      <span className={`px-2.5 py-1 text-xs font-medium border ${
                        profile.two_factor_enabled
                          ? 'bg-success-50 text-success-700 border-success-200'
                          : 'bg-slate-100 text-slate-600 border-slate-200'
                      }`}>
                        {profile.two_factor_enabled ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Button variant="outline">
                      <Shield className="w-4 h-4 mr-2" />
                      {profile.two_factor_enabled ? '2FA Ayarları' : '2FA Etkinleştir'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notifications and Privacy tabs removed for customer panel requirements */}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
